import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Spin } from 'antd';
import PageLoader from '../../../../components/PageLoader/PageLoader';
import PlanProcessBar from '../../../../components/ProcessBar/PlanProcessBar';
import InfoBox from '../../../../components/InfoBox/InfoBox';
import ModuleEmptyState from '../../../../components/ModuleEmptyState';
import TeamType from './components/TeamType';
import { fetchTeam } from './TeamAction';
import { fetchContact } from '../../../Organisation/scenes/Contact/ContactAction';
import { orgIdSelector } from '../../../Organisation/OrganisationSelectors';
import { planIdSelector } from '../Plan/PlanSelectors';
import { teamListSelector, loadingTeamsSelector, teamsFetchStatusSelector } from './TeamSelectors';
import { loadingContactsSelector } from '../../../Organisation/scenes/Contact/ContactSelectors';
import './Team.scss';

const INFO_MSG = (
  <>
    <p>
      There are typically 3 teams that support the Business Continuity Governance Committee and report to that
      committee. These are
    </p>
    <ol>
      <li>Crisis Management Team</li>
      <li>Information Technology Team</li>
      <li>Emergency Response Team</li>
    </ol>
    <p>The purpose and role of each team is explained as you complete the details below.</p>
  </>
);

const TEAMS = {
  'Emergency Response Team': {
    id: 'emergency-response-team',
    guidance: (
      <>
        <strong>What to do:</strong> assign contacts to the functional roles required for emergency response using{' '}
        <strong>Add Member</strong> or <strong>Add new contact</strong>.
      </>
    ),
    detail:
      "The composition of your Emergency Response Teams is important so as to identify the possible functional areas and the team member's role. The Response Controller will determine which functions are required during a specific response.",
  },
  'Information Technology Team': {
    id: 'information-technology-team',
    guidance: (
      <>
        <strong>What to do:</strong> add IT contacts who will support disaster recovery and continuity using{' '}
        <strong>Add Member</strong> or <strong>Add new contact</strong>.
      </>
    ),
    detail:
      'The Information Technology Team is going to have a great responsibility for certain sections of your Business Continuity Plan (particularly related to Disaster Recovery). The Team may consist of internal staff or contractors where you outsource your Information and Communication Technology.',
  },
  'Crisis Management Team': {
    id: 'crisis-management-team',
    guidance: (
      <>
        <strong>What to do:</strong> list executives who can lead crisis decisions using <strong>Add Member</strong> or{' '}
        <strong>Add new contact</strong>.
      </>
    ),
    detail: (
      <>
        <p>
          The Crisis Management Team consists of a small group of executives who have the resources, ability and
          authority to do whatever is necessary to resolve a crisis. The team will take operational ownership of the
          crisis response when a crisis has occurred or when a possible or probable crisis situation will exist.
        </p>
        <p>
          The Crisis Management Team is the highest level decision making authority at times of crisis. It has
          unrestricted authority to respond in the best interest of all stakeholders and its crisis response decisions
          will supersede the existence or normal interpretation of all or any policy or standard operating procedures.
        </p>
        <p>
          If the Organisation plan covers multiple subsidiary organisations that have different directors or senior
          executives, these should be added here as this list even though they may not be responsible in each and every
          business continuity event.
        </p>
      </>
    ),
  },
};

function getTeamsEmptyMessage(fetchStatus) {
  if (fetchStatus === 'payment_required') {
    return 'Your plan has expired. Please renew your plan subscription to view this section.';
  }

  if (fetchStatus === 'not_found') {
    return 'Teams are not available for this plan yet. Complete your plan setup or try again later.';
  }

  if (fetchStatus === 'error') {
    return 'Unable to load teams. Please refresh the page or try again.';
  }

  return 'No teams are configured for this plan yet.';
}

const Team = ({
  teamList,
  teamsFetchStatus,
  teamsLoading,
  contactsLoading,
  orgId,
  planId,
  fetchTeam,
  fetchContact,
  planScreenTitle,
}) => {
  useEffect(() => {
    fetchTeam(planId);
  }, [fetchTeam, planId]);

  useEffect(() => {
    fetchContact(orgId);
  }, [fetchContact, orgId]);

  const scrollToAnchor = () => {
    let anchorName = window.location.hash;
    if (anchorName) {
      anchorName = anchorName.replace('#', '');
      const anchorElement = document.getElementById(anchorName);
      if (anchorElement) {
        window.scrollTo(0, anchorElement.offsetTop);
      }
    }
  };

  useEffect(() => {
    if (!teamsLoading && !contactsLoading) {
      scrollToAnchor();
    }
  }, [teamsLoading, contactsLoading]);

  return (
    <>
      <PlanProcessBar color="#5BC2E7" title={planScreenTitle || 'Teams'} />
      <InfoBox
        color="#5BC2E7"
        guidance={
          <>
            <strong>What to do:</strong> expand each team below, review its responsibilities, and assign the appropriate
            contacts using <strong>Add Member</strong>. Every team must have at least one contact assigned.
          </>
        }
        message={INFO_MSG}
      />
      {!teamsLoading && !contactsLoading ? (
        <Spin spinning={teamsLoading || contactsLoading}>
          <div className="component-wrapper">
            {teamList && teamList.length > 0 ? (
              teamList.map((team) => {
                const meta = TEAMS[team.teamType?.name] || {};
                return (
                  <div key={team.teamId} id={meta.id}>
                    <TeamType team={team} teamGuidance={meta.guidance} teamDetail={meta.detail} />
                  </div>
                );
              })
            ) : (
              <ModuleEmptyState
                moduleTitle="Teams"
                embedded
                headline={
                  teamsFetchStatus === 'payment_required'
                    ? 'Plan subscription required'
                    : teamsFetchStatus === 'error'
                      ? 'Unable to load teams'
                      : 'No teams configured yet'
                }
                description={getTeamsEmptyMessage(teamsFetchStatus)}
                tip="Tip: teams are created with your plan. Once available, use Add Member on each team to assign contacts."
                iconType="team"
              />
            )}
          </div>
        </Spin>
      ) : (
        <PageLoader tip="Loading Teams..." />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  orgId: orgIdSelector(state),
  planId: planIdSelector(state),
  teamList: teamListSelector(state),
  teamsFetchStatus: teamsFetchStatusSelector(state),
  teamsLoading: loadingTeamsSelector(state),
  contactsLoading: loadingContactsSelector(state),
});

const mapDispatchToProps = {
  fetchContact,
  fetchTeam,
};

export default connect(mapStateToProps, mapDispatchToProps)(Team);
